// Store dei PERSONAL TASKS (obiettivi / task personali)
import { create } from 'zustand';
import type { PersonalTask, TaskStatus, Priority } from '@/types';
import { getDatabase } from '@/lib/db';
import { genId, nowISO } from '@/utils';
import { pushPersonalToCloud, deletePersonalFromCloud } from './syncHelpers';

interface PersonalTasksState {
  tasks: PersonalTask[];
  loading: boolean;
  load: () => Promise<void>;
  create: (data: Partial<PersonalTask>) => Promise<PersonalTask>;
  update: (id: string, patch: Partial<PersonalTask>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  move: (id: string, status: TaskStatus, position: number) => Promise<void>;
  reorder: (status: TaskStatus, orderedIds: string[]) => Promise<void>;
  replaceAll: (tasks: PersonalTask[]) => Promise<void>;
}

export const usePersonalTasksStore = create<PersonalTasksState>((set, get) => ({
  tasks: [],
  loading: false,

  load: async () => {
    set({ loading: true });
    try {
      const db = getDatabase();
      const tasks = await db.getAllPersonalTasks();
      set({ tasks, loading: false });
    } catch (e) {
      console.error('[personal] load error', e);
      set({ loading: false });
    }
  },

  create: async (data) => {
    const now = nowISO();
    const maxPos = get().tasks.filter((t) => t.status === (data.status ?? 'todo')).reduce((m, t) => Math.max(m, t.position), -1);
    const task: PersonalTask = {
      id: genId(),
      title: data.title ?? '',
      description: data.description ?? '',
      status: (data.status ?? 'todo') as TaskStatus,
      priority: (data.priority ?? 'medium') as Priority,
      due_date: data.due_date ?? null,
      tags: data.tags ?? [],
      position: data.position ?? maxPos + 1,
      created_at: now,
      updated_at: now,
    };
    const db = getDatabase();
    await db.upsertPersonalTask(task);
    set({ tasks: [...get().tasks, task] });
    pushPersonalToCloud(task).catch(() => {});
    return task;
  },

  update: async (id, patch) => {
    const current = get().tasks.find((t) => t.id === id);
    if (!current) return;
    const updated: PersonalTask = { ...current, ...patch, updated_at: nowISO() };
    const db = getDatabase();
    await db.upsertPersonalTask(updated);
    set({ tasks: get().tasks.map((t) => (t.id === id ? updated : t)) });
    pushPersonalToCloud(updated).catch(() => {});
  },

  remove: async (id) => {
    const db = getDatabase();
    await db.deletePersonalTask(id);
    set({ tasks: get().tasks.filter((t) => t.id !== id) });
    deletePersonalFromCloud(id).catch(() => {});
  },

  move: async (id, status, position) => {
    await get().update(id, { status, position });
  },

  reorder: async (status, orderedIds) => {
    const db = getDatabase();
    const tasks = get().tasks;
    const updates: PersonalTask[] = [];
    orderedIds.forEach((id, idx) => {
      const t = tasks.find((x) => x.id === id);
      if (t && (t.position !== idx || t.status !== status)) {
        const updated: PersonalTask = { ...t, status, position: idx, updated_at: nowISO() };
        updates.push(updated);
      }
    });
    for (const u of updates) await db.upsertPersonalTask(u);
    set({
      tasks: tasks.map((t) => {
        const u = updates.find((x) => x.id === t.id);
        return u ?? t;
      }),
    });
  },

  replaceAll: async (tasks) => {
    const db = getDatabase();
    await db.replaceAllPersonalTasks(tasks);
    set({ tasks: await db.getAllPersonalTasks() });
  },
}));
